import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listStudents } from "../../api/studentApi";
import { listInvoicesBetween } from "../../api/feeApi";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");
const groupApi = createLookupApi("groups");
const WIDE_FROM = "1900-01-01";
const WIDE_TO = "2099-12-31";

/**
 * Direct port of digicampusbd's `fee-report-sub-head-yearly-paid-list`
 * ("Fee Sub Paid List") — for one academic year + section (+ optional
 * group(s)), each student's total paid amount per fee sub-head across every
 * invoice under that identification (no date range in the old app's own
 * request — it queries by identification, not by payment date). Confirmed
 * via `FeeReportService.showsectionTotalPaidDetailsInSubHead` that this is a
 * plain SUM per (student, fee-subhead) — a Spring-Data derived query, not
 * even native SQL — with a fixed 35-column layout the old app pre-allocates;
 * this port shows whichever subheads actually appear instead of a fixed 35
 * slots, and skips the old app's optional fee-subhead multi-select filter
 * (shows every subhead with a real payment) as a documented simplification —
 * see MIGRATION.md. Built entirely client-side by cross-referencing the
 * already-exposed `GET /api/students` with `GET /api/fee-invoices/range`
 * (called with a maximal date span, since an identification only ever
 * belongs to one academic year) — no new backend needed.
 *
 * Real gap found and fixed while sweeping Fee Management: the real request
 * DTO's own `groupIds` field (`SectionTotalPaidDetailsInSubHeadRequest`) and
 * its real template's own group filter (`/group/configuration/list/by/
 * classconfig-id`) are both a **multi-select** of groups, not a single
 * dropdown — this page originally only let staff narrow to one group or
 * every group, losing the ability to combine an arbitrary subset (e.g.
 * "Science + Commerce but not Arts") in one search. Converted to checkboxes,
 * matching the pattern already used for every other multi-select filter in
 * this migration (ledgers, fee sub-heads).
 */
export default function FeeSubHeadYearlyPaidListPage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupIds, setGroupIds] = useState([]);
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    groupApi.list().then(setGroups);
  }, []);

  const toggleGroup = (id) => setGroupIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSearch = async () => {
    setError("");
    setRawData(null);
    if (!academicYear || !classConfigId) {
      setError("Please select academic year and section.");
      return;
    }
    setLoading(true);
    try {
      const [students, invoices] = await Promise.all([
        listStudents(academicYear, classConfigId),
        listInvoicesBetween(WIDE_FROM, WIDE_TO),
      ]);
      const filteredStudents =
        groupIds.length === 0 ? students : students.filter((s) => groupIds.includes(String(s.groupId)));
      setRawData({ students: filteredStudents, invoices });
    } catch (err) {
      setError("Failed to load fee sub-head paid list.");
    } finally {
      setLoading(false);
    }
  };

  const { subHeadNames, rows } = useMemo(() => {
    if (!rawData) return { subHeadNames: [], rows: [] };
    const { students, invoices } = rawData;
    const byIdentification = new Map(students.map((s) => [String(s.identificationId), s]));
    const headSet = new Set();
    const perStudent = new Map();

    invoices.forEach((inv) => {
      const student = byIdentification.get(String(inv.identificationId));
      if (!student) return;
      (inv.lines || []).forEach((line) => {
        if (!line.feeSubHeadName) return;
        headSet.add(line.feeSubHeadName);
        const key = String(student.identificationId);
        if (!perStudent.has(key)) perStudent.set(key, {});
        const bucket = perStudent.get(key);
        bucket[line.feeSubHeadName] = (bucket[line.feeSubHeadName] || 0) + (line.paidAmount || 0);
      });
    });

    const names = Array.from(headSet).sort();
    const rowList = students
      .map((s) => {
        const details = perStudent.get(String(s.identificationId)) || {};
        const total = names.reduce((sum, h) => sum + (details[h] || 0), 0);
        return { student: s, details, total };
      })
      .filter((r) => r.total > 0)
      .sort((a, b) => (a.student.studentRoll || 0) - (b.student.studentRoll || 0));
    return { subHeadNames: names, rows: rowList };
  }, [rawData]);

  const grandTotal = rows.reduce((s, r) => s + r.total, 0);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Fee Sub Paid List</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Select Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>

      {groups.length > 0 && (
        <div className="form-row">
          <span className="admit-card-label">Filter by Group (none selected = all groups):</span>{" "}
          {groups.map((g) => (
            <label key={g.id} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 12 }}>
              <input type="checkbox" checked={groupIds.includes(String(g.id))} onChange={() => toggleGroup(String(g.id))} />
              {g.name}
            </label>
          ))}
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        rawData && (
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Student ID</th>
                  <th>Student Name</th>
                  <th>Roll</th>
                  {subHeadNames.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.student.identificationId}>
                    <td>{r.student.customStudentId}</td>
                    <td>{r.student.studentName}</td>
                    <td>{r.student.studentRoll}</td>
                    {subHeadNames.map((h) => (
                      <td key={h}>{(r.details[h] || 0).toFixed(2)}</td>
                    ))}
                    <td>{r.total.toFixed(2)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={subHeadNames.length + 4}>No paid sub-heads found for this selection.</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={subHeadNames.length + 3} style={{ fontWeight: 800, textAlign: "right" }}>
                    Grand Total
                  </td>
                  <td>
                    <strong>{grandTotal.toFixed(2)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}
    </MainLayout>
  );
}
